import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useVideoStore } from '../../../../../stores/useVideoStore';
import { useSubscriptionLimits } from '../../../../../hooks/useSubscriptionLimits';
import { useTelegramWebApp } from '../../../../../hooks/useTelegramWebApp';
import { useIsAdmin } from '../../../../../hooks/useIsAdmin';
import { useModelStatusesStore } from '../../../../../stores/useModelStatusesStore';
import { VIDEO_MODELS_CONFIG } from '../../../../../config/models';
import { MODEL_TO_LIMIT_KEY as FALLBACK_MODEL_TO_LIMIT_KEY } from '../../../../../config/videoLimits';
import { SUBSCRIPTION_TIERS } from '../../../../../config/subscription';
import { useNavigate } from 'react-router-dom';
import { ModelStatusBadge } from '../../../../ModelStatus/ModelStatusBadge';
import { AdminStatusButton } from '../../../../ModelStatus/AdminStatusButton';
import type { FC, SVGProps } from 'react';
import {
  SoraProviderIcon,
  FireIcon,
  VIDEO_PROVIDER_ICON_COMPONENTS,
} from '../../../VideoIcons';
import styles from './VideoModelSelectorChip.module.css';

/** Tier hierarchy for determining minimum required tier */
const TIER_ORDER = ['free', 'start', 'basic', 'pro', 'max'] as const;

/** Determine the minimum subscription tier required for a model.
 * Старт даёт Basic-level access — приводим к 'basic' для бейджа. */
const getMinimumTier = (modelId: string): 'free' | 'basic' | 'pro' | null => {
  for (const tier of TIER_ORDER) {
    const config = SUBSCRIPTION_TIERS[tier];
    if (config?.limits?.allowedVideoModels?.includes(modelId)) {
      if (tier === 'free') return 'free';
      if (tier === 'start' || tier === 'basic') return 'basic';
      return tier as 'pro';
    }
  }
  return null; // Not in any tier
};

/** Badge info for locked models */
const TIER_BADGE_CONFIG: Record<string, { label: string; color: string }> = {
  basic: { label: 'BASIC', color: '#FFFFFF' },
  pro: { label: 'PRO', color: '#1b95e0' },
};

type IconProps = SVGProps<SVGSVGElement>;

// Provider grouping configuration (mirrors Image ModelSelector pattern)
interface ProviderGroup {
  id: string;
  name: string;
  icon: FC<IconProps>;
  description?: string;
  models: string[]; // model IDs from VIDEO_MODELS_CONFIG.<provider>.models
}

const PROVIDER_ICONS: Record<string, FC<IconProps>> = VIDEO_PROVIDER_ICON_COMPONENTS;

// Provider description keys (used with t() inside component)
const PROVIDER_DESCRIPTION_KEYS: Record<string, string> = {
  minimax: 'modelSelector.providerDesc.minimax',
  kling: 'modelSelector.providerDesc.kling',
  sora: 'modelSelector.providerDesc.sora',
  veo: 'modelSelector.providerDesc.veo',
  higgsfield: 'modelSelector.providerDesc.higgsfield',
  wan: 'modelSelector.providerDesc.wan',
  seedance: 'modelSelector.providerDesc.seedance',
  grok: 'modelSelector.providerDesc.grok',
  runway: 'modelSelector.providerDesc.runway',
};

// Provider order (user-specified)
const BASE_PROVIDER_ORDER = ['kling', 'seedance', 'grok', 'veo', 'sora', 'higgsfield', 'runway'];
const EXTRA_PROVIDERS = Object.keys(VIDEO_MODELS_CONFIG).filter(
  (provider) => !BASE_PROVIDER_ORDER.includes(provider)
);
const PROVIDER_ORDER = [...BASE_PROVIDER_ORDER, ...EXTRA_PROVIDERS];

// Video provider groups with their model IDs (derived from config)
const PROVIDER_GROUPS: ProviderGroup[] = PROVIDER_ORDER.map((provider) => {
  const config = VIDEO_MODELS_CONFIG[provider];
  return {
    id: provider,
    name: config?.name || provider,
    icon: PROVIDER_ICONS[provider],
    description: PROVIDER_DESCRIPTION_KEYS[provider] || '',
    models: config?.models?.map((model) => model.id) || [],
  };
});

// Model ID -> provider group mapping
const MODEL_TO_PROVIDER: Record<string, string> = {};
Object.entries(VIDEO_MODELS_CONFIG).forEach(([provider, config]) => {
  config.models.forEach((model) => {
    MODEL_TO_PROVIDER[model.id] = provider;
  });
});

// ==================== EDIT MODE MODELS ====================
const EDIT_MODELS_CONFIG = [
  { id: 'kling-o3', name: 'Kling O3', provider: 'kling', descKey: 'sidebar.editModel.klingO3Desc' },
  { id: 'kling-o1', name: 'Kling O1', provider: 'kling', descKey: 'sidebar.editModel.klingO1Desc' },
  { id: 'runway-aleph', name: 'Runway Aleph', provider: 'runway', descKey: 'sidebar.editModel.runwayAlephDesc' },
  { id: 'grok-imagine-video', name: 'Grok Imagine', provider: 'grok', descKey: 'sidebar.editModel.grokImagineDesc' },
];

export const EDIT_MODEL_IDS = EDIT_MODELS_CONFIG.map(m => m.id);

/**
 * VideoModelSelectorChip - Compact pill [ProviderIcon + ModelName + Chevron]
 * Opens a portal dropdown with two-level drill-down (Providers > Models)
 * When editMode=true, shows a flat list of 3 edit models (no provider grouping)
 */
export const VideoModelSelectorChip = ({ editMode }: { editMode?: boolean } = {}) => {
  const { t } = useTranslation('video');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedProviderGroup, setSelectedProviderGroup] = useState<string | null>(null);
  const [hoveredProvider, setHoveredProvider] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [subFlyoutPos, setSubFlyoutPos] = useState({ top: 0, left: 0 });
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    currentProvider,
    currentModel,
    modelSettings,
    setProvider,
    setModel,
    setRatio,
    setQuality,
    setDuration,
  } = useVideoStore();

  const { limits, tier, modelMappings } = useSubscriptionLimits();
  const { haptic } = useTelegramWebApp();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const fetchStatuses = useModelStatusesStore(state => state.fetchStatuses);
  const isModelUnderMaintenance = useModelStatusesStore(state => state.isModelUnderMaintenance);
  const getRecommendedModels = useModelStatusesStore(state => state.getRecommendedModels);

  // Load model statuses on mount
  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);

  // Get recommended model IDs for "Featured" section
  const recommendedModelIds = useMemo(() => getRecommendedModels('video'), [getRecommendedModels]);

  const MODEL_TO_LIMIT_KEY = modelMappings?.video || FALLBACK_MODEL_TO_LIMIT_KEY;

  // Check model availability (backend limits + client-side subscription fallback)
  const isModelAvailable = useCallback((modelId: string): boolean => {
    // Client-side subscription check (always enforced as safety net)
    const currentTier = tier || 'free';
    const tierConfig = SUBSCRIPTION_TIERS[currentTier as keyof typeof SUBSCRIPTION_TIERS];
    if (tierConfig?.limits?.allowedVideoModels && !tierConfig.limits.allowedVideoModels.includes(modelId)) {
      return false;
    }

    // Backend limits check
    if (!limits?.video) return true;
    if (tier === 'free' && !limits.video.enabled) return false;
    const limitKey = MODEL_TO_LIMIT_KEY[modelId];
    if (!limitKey) return true;
    const providerConfig = limits.video.allowedProviders?.[limitKey];
    return providerConfig?.enabled || false;
  }, [limits, tier, MODEL_TO_LIMIT_KEY]);

  // Get current provider group
  const currentProviderGroupId = MODEL_TO_PROVIDER[currentModel];
  const currentProviderGroup = PROVIDER_GROUPS.find(g => g.id === (currentProviderGroupId || currentProvider));

  // Get model display name - strip provider prefix, show only version/variant
  const getModelDisplayName = (modelId: string, provider: string): string => {
    const config = VIDEO_MODELS_CONFIG[provider];
    if (!config?.models) return modelId;
    const model = config.models.find(m => m.id === modelId);
    if (!model) return modelId;

    const name = model.name;

    // 1) Try stripping the config display name (e.g. "Runway" from "Runway Gen-4 Turbo")
    const configName = config.name;
    if (configName && name.startsWith(configName + ' ')) {
      return name.substring(configName.length + 1);
    }

    // 2) Try stripping the provider key capitalised (e.g. "Kling" from "Kling 2.6" when config.name is "Kling AI")
    const providerCapitalized = provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase();
    if (name.startsWith(providerCapitalized + ' ')) {
      return name.substring(providerCapitalized.length + 1);
    }

    // 3) Try stripping first word of config name (e.g. "Kling" from "Kling AI")
    const firstWord = configName?.split(' ')[0];
    if (firstWord && firstWord.length > 2 && name.startsWith(firstWord + ' ')) {
      return name.substring(firstWord.length + 1);
    }

    return name;
  };

  const currentModelText = getModelDisplayName(currentModel, currentProvider);

  // Get model info badges (quality, duration, frames, audio) with icons
  const getModelInfo = (modelId: string, provider: string): Array<{ text: string; iconClass?: string }> => {
    const config = VIDEO_MODELS_CONFIG[provider];
    if (!config?.models) return [];
    const model = config.models.find(m => m.id === modelId);
    if (!model) return [];
    const params = model.parameters;
    const chips: Array<{ text: string; iconClass?: string }> = [];

    // Quality (gem icon — same as gallery ParametersBlock)
    if (params.quality?.length > 0) {
      let qualityText: string;
      if (modelId.startsWith('seedance-') && params.quality.includes('480p') && params.quality.includes('1080p')) {
        qualityText = '480-1080p';
      } else if (provider === 'kling' && params.quality.includes('std') && params.quality.includes('pro')) {
        qualityText = '720-1080p';
      } else {
        qualityText = params.quality.join(', ');
      }
      chips.push({ text: qualityText, iconClass: 'fa-solid fa-gem' });
    }

    // Duration (stopwatch icon — same as gallery ParametersBlock)
    if (params.duration?.length > 0) {
      const durations = params.duration.map(d => parseInt(d));
      const min = Math.min(...durations);
      const max = Math.max(...durations);
      chips.push({ text: min === max ? `${min}${t('composer.secSuffix')}` : `${min}-${max}${t('composer.secSuffix')}`, iconClass: 'fa-solid fa-stopwatch' });
    }

    // Frame types (image icon)
    if (modelId === 'runway-aleph') {
      chips.push({ text: t('modelSelector.videoChip'), iconClass: 'fa-solid fa-film' });
    } else if (params.frameTypes?.length > 0) {
      if (params.frameTypes.includes('start') && params.frameTypes.includes('end')) {
        chips.push({ text: 'Start/End', iconClass: 'fa-solid fa-image' });
      } else {
        chips.push({ text: params.frameTypes[0] === 'start' ? 'Start' : 'End', iconClass: 'fa-solid fa-image' });
      }
    }

    // Audio (volume icon)
    if (params.supportsAudio) {
      chips.push({ text: t('modelSelector.audioChip'), iconClass: 'fa-solid fa-volume-high' });
    }

    return chips;
  };

  // Sort provider groups: allowed first
  const sortedProviderGroups = useMemo(() => {
    return PROVIDER_GROUPS.filter(group => {
      const config = VIDEO_MODELS_CONFIG[group.id];
      return config && config.models && config.models.length > 0;
    }).sort((a, b) => {
      const aConfig = VIDEO_MODELS_CONFIG[a.id];
      const bConfig = VIDEO_MODELS_CONFIG[b.id];
      const aHasAllowed = aConfig?.models?.some(m => m.available && isModelAvailable(m.id)) || false;
      const bHasAllowed = bConfig?.models?.some(m => m.available && isModelAvailable(m.id)) || false;
      if (aHasAllowed === bHasAllowed) return 0;
      return aHasAllowed ? -1 : 1;
    });
  }, [isModelAvailable]);

  // Get models for selected provider group
  const selectedGroupModels = useMemo(() => {
    if (!selectedProviderGroup) return [];
    const group = PROVIDER_GROUPS.find(g => g.id === selectedProviderGroup);
    if (!group) return [];
    const config = VIDEO_MODELS_CONFIG[group.id];
    if (!config?.models) return [];

    return config.models.map(model => ({
      id: model.id,
      name: model.name,
      displayName: getModelDisplayName(model.id, group.id),
      isLocked: !model.available || !isModelAvailable(model.id) || isModelUnderMaintenance(model.id, 'video'),
      isMaintenance: isModelUnderMaintenance(model.id, 'video'),
      infoChips: getModelInfo(model.id, group.id),
    }));
  }, [selectedProviderGroup, isModelAvailable, isModelUnderMaintenance]);

  const handleMenuToggle = useCallback(() => {
    if (!isMenuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const isMobile = viewportWidth <= 480;
      const menuWidth = isMobile ? viewportWidth - 32 : 280;

      let left: number;
      if (isMobile) {
        left = 16;
      } else {
        left = rect.left;
        if (left + menuWidth > viewportWidth - 16) {
          left = viewportWidth - menuWidth - 16;
        }
        if (left < 16) left = 16;
      }

      setDropdownPos({ top: rect.top, left });
    }

    haptic.impact('light');
    setIsMenuOpen(prev => !prev);
    setSelectedProviderGroup(null);
  }, [haptic, isMenuOpen]);

  const handleProviderGroupClick = useCallback((group: ProviderGroup) => {
    haptic.impact('light');
    // Always drill-down — consistent «provider → model» pattern across all providers
    setSelectedProviderGroup(group.id);
  }, [haptic]);

  const handleBackToProviders = useCallback(() => {
    haptic.impact('light');
    setSelectedProviderGroup(null);
  }, [haptic]);

  const selectModel = useCallback((provider: string, modelId: string) => {
    haptic.impact('medium');
    if (provider !== currentProvider) {
      setProvider(provider);
    }
    setModel(modelId);

    // Apply default parameters from model config
    const config = VIDEO_MODELS_CONFIG[provider];
    const model = config?.models.find(m => m.id === modelId);
    if (model?.parameters) {
      const params = model.parameters;
      const savedSettings = modelSettings?.[provider];

      if (savedSettings) {
        if (savedSettings.ratio && params.ratio.includes(savedSettings.ratio)) {
          setRatio(savedSettings.ratio);
        } else if (params.ratio?.length > 0) {
          setRatio(params.ratio[0]);
        }
        if (savedSettings.quality && params.quality.includes(savedSettings.quality)) {
          setQuality(savedSettings.quality);
        } else if (params.quality?.length > 0) {
          setQuality(params.quality[0]);
        } else {
          setQuality('');
        }
        if (savedSettings.duration && params.duration.includes(savedSettings.duration)) {
          setDuration(savedSettings.duration);
        } else if (params.duration?.length > 0) {
          setDuration(params.duration[0]);
        }
      } else {
        if (params.ratio?.length > 0) setRatio(params.ratio[0]);
        if (params.quality?.length > 0) setQuality(params.quality[0]); else setQuality('');
        if (params.duration?.length > 0) setDuration(params.duration[0]);
      }
    }

    setIsMenuOpen(false);
    setSelectedProviderGroup(null);
  }, [haptic, currentProvider, modelSettings, setProvider, setModel, setRatio, setQuality, setDuration]);

  const handleModelSelect = useCallback((modelId: string, isLocked: boolean) => {
    if (isLocked) {
      // Navigate to pricing page when model is locked
      setIsMenuOpen(false);
      setSelectedProviderGroup(null);
      navigate('/pricing');
      return;
    }
    if (!selectedProviderGroup) return;
    selectModel(selectedProviderGroup, modelId);
  }, [selectModel, selectedProviderGroup, navigate]);

  // Build featured models list from recommended statuses
  const featuredModels = useMemo(() => {
    if (recommendedModelIds.length === 0) return [];
    const items: { id: string; name: string; displayName: string; provider: string; providerIcon: FC<IconProps>; infoChips: Array<{ text: string; iconClass?: string }>; isLocked: boolean }[] = [];
    for (const modelId of recommendedModelIds) {
      const provider = MODEL_TO_PROVIDER[modelId];
      if (!provider) continue;
      const config = VIDEO_MODELS_CONFIG[provider];
      const model = config?.models.find(m => m.id === modelId);
      if (!model || !model.available) continue;
      items.push({
        id: model.id,
        name: model.name,
        displayName: getModelDisplayName(model.id, provider),
        provider,
        providerIcon: PROVIDER_ICONS[provider] || SoraProviderIcon,
        infoChips: getModelInfo(model.id, provider),
        isLocked: !isModelAvailable(model.id) || isModelUnderMaintenance(model.id, 'video'),
      });
    }
    return items;
  }, [recommendedModelIds, isModelAvailable, isModelUnderMaintenance]);

  const CurrentProviderIcon = currentProviderGroup?.icon || PROVIDER_ICONS[currentProvider] || SoraProviderIcon;

  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;

  const handleProviderHover = useCallback((groupId: string, el: HTMLElement) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      const mainRect = dropdownRef.current?.getBoundingClientRect();
      if (!mainRect) return;
      const itemRect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const subWidth = 280;
      let left = mainRect.right + 6;
      if (left + subWidth > vw - 16) left = Math.max(16, mainRect.left - subWidth - 6);
      // A2: bottom of sub-flyout aligns with bottom of hovered provider item
      const bottom = itemRect.bottom;
      setSubFlyoutPos({ top: bottom, left });
      setHoveredProvider(groupId);
    }, 120);
  }, []);

  const handleProviderLeave = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setHoveredProvider(null), 200);
  }, []);

  const handleSubFlyoutEnter = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  }, []);

  const handleSubFlyoutLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => setHoveredProvider(null), 200);
  }, []);

  return (
    <>
      <div className={styles.modelSelectorContainer}>
        <button
          ref={buttonRef}
          type="button"
          className={clsx(styles.modelSelectorButton, { [styles.active]: isMenuOpen })}
          onClick={handleMenuToggle}
          title={t('modelSelector.selectModelTitle')}
        >
          <CurrentProviderIcon className={styles.modelProviderIcon} />
          <span className={styles.modelName}>{currentModelText}</span>
          <svg
            className={clsx(styles.modelChevron, { [styles.rotated]: isMenuOpen })}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        <div className={styles.modelDivider} />
      </div>

      {/* Dropdown Menu (Portal) - opens upward */}
      {isMenuOpen && createPortal(
        <>
          <div
            className={styles.modelBackdrop}
            onClick={() => {
              setIsMenuOpen(false);
              setSelectedProviderGroup(null);
              setHoveredProvider(null);
            }}
          />
          {/* Desktop: Main provider flyout */}
          <div
            ref={dropdownRef}
            className={styles.modelDropdown}
            style={{
              left: dropdownPos.left,
              top: dropdownPos.top,
              transform: 'translateY(calc(-100% - 8px))',
              ...(isDesktop ? { width: 320, maxHeight: 'min(520px, calc(100vh - 32px))' } : {}),
            }}
          >
            {/* ===== EDIT MODE: Flat list of 3 edit models ===== */}
            {editMode ? (
              <>
                <div className={styles.modelDropdownHeader}>
                  <span className={styles.modelDropdownTitle}>{t('modelSelector.chooseModel')}</span>
                </div>
                <div className={styles.modelDropdownScroll}>
                  {EDIT_MODELS_CONFIG.map((model) => {
                    const isActive = model.id === currentModel;
                    const ProviderIcon = PROVIDER_ICONS[model.provider] || SoraProviderIcon;
                    return (
                      <button
                        key={model.id}
                        className={clsx(styles.modelDropdownItem, { [styles.active]: isActive })}
                        onClick={() => selectModel(model.provider, model.id)}
                      >
                        <div className={clsx(styles.modelIconWrapper, { [styles.active]: isActive })}>
                          <ProviderIcon className={styles.modelDropdownIcon} />
                        </div>
                        <div className={styles.modelItemInfo}>
                          <span className={styles.modelItemLabel}>{model.name}</span>
                          <span className={styles.modelLockedText}>{t(model.descKey)}</span>
                        </div>
                        {isActive && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--selector-check-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', flexShrink: 0 }}>
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : isDesktop ? (
              <>
                <div className={styles.modelDropdownHeader}>
                  <span className={styles.modelDropdownTitle}>{t('modelSelector.chooseModel')}</span>
                </div>
                <div className={styles.modelDropdownScroll}>
                  {/* Featured Models Section (Desktop) */}
                  {featuredModels.length > 0 && (
                    <>
                      <div className={styles.modelSectionHeader}>
                        <FireIcon className={styles.modelSectionHeaderIcon} />
                        <span>{t('modelSelector.topModels')}</span>
                      </div>
                      {featuredModels.map((model) => {
                        const isActive = model.id === currentModel;
                        return (
                          <div key={`desktop-featured-${model.id}`} style={{ position: 'relative' }}>
                            <button
                              className={clsx(
                                styles.modelDropdownItem,
                                { [styles.active]: isActive },
                                { [styles.locked]: model.isLocked }
                              )}
                              onClick={() => {
                                if (model.isLocked) { navigate('/pricing'); setIsMenuOpen(false); return; }
                                selectModel(model.provider, model.id);
                              }}
                              /* disabled handled via onClick + CSS */
                            >
                              <div className={clsx(styles.modelIconWrapper, { [styles.active]: isActive })}>
                                <model.providerIcon className={styles.modelDropdownIcon} />
                              </div>
                              <div className={styles.modelItemInfo}>
                                <span className={styles.modelItemLabel}>
                                  {model.name}
                                  <ModelStatusBadge modelId={model.id} modelType="video" context="selector" compact />
                                </span>
                                {model.infoChips.length > 0 && (
                                  <div className={styles.modelInfoRow}>
                                    {model.infoChips.map((chip, idx) => (
                                      <span key={idx} className={styles.modelInfoChip}>{chip.iconClass && <i className={chip.iconClass} />}{chip.text}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {isActive && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--selector-check-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', flexShrink: 0 }}><polyline points="20 6 9 17 4 12" /></svg>}
                            </button>
                            <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'auto' }}>
                              <AdminStatusButton modelId={model.id} modelType="video" modelDisplayName={model.name} isAdmin={isAdmin} compact />
                            </div>
                          </div>
                        );
                      })}
                      <div className={styles.modelSectionHeader}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                        <span>{t('modelSelector.allModels')}</span>
                      </div>
                    </>
                  )}
                  {sortedProviderGroups.map((group) => {
                    const config = VIDEO_MODELS_CONFIG[group.id];
                    const allModels = config?.models?.filter(m => m.available) || [];
                    const isActive = currentProviderGroupId === group.id || currentProvider === group.id;
                    const hasModels = allModels.length > 0;
                    const isClickable = hasModels;
                    const isHovered = hoveredProvider === group.id;

                    return (
                      <button
                        key={group.id}
                        className={clsx(
                          styles.modelDropdownItem,
                          { [styles.active]: isActive },
                          { [styles.hovered]: isHovered }
                        )}
                        onMouseEnter={(e) => isClickable && handleProviderHover(group.id, e.currentTarget as HTMLElement)}
                        onMouseLeave={handleProviderLeave}
                        disabled={!isClickable}
                      >
                        <div className={clsx(styles.modelIconWrapper, { [styles.active]: isActive })}>
                          <group.icon className={styles.modelDropdownIcon} />
                        </div>
                        <div className={styles.modelItemInfo}>
                          <span className={styles.modelItemLabel}>{group.name}</span>
                          {group.description && (
                            <span className={styles.modelLockedText}>{t(group.description)}</span>
                          )}
                        </div>
                        {hasModels && (
                          <span className={styles.modelMenuArrow}>›</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              /* Mobile < 1024px: Original click-to-drill-down */
              <>
                {/* First Level: Featured + Provider Groups */}
                {!selectedProviderGroup && (
              <>
                <div className={styles.modelDropdownHeader}>
                  <span className={styles.modelDropdownTitle}>{t('modelSelector.chooseModel')}</span>
                </div>
                <div className={styles.modelDropdownScroll}>
                  {/* Featured Models Section */}
                  {featuredModels.length > 0 && (
                    <>
                      <div className={styles.modelSectionHeader}>
                        <FireIcon className={styles.modelSectionHeaderIcon} />
                        <span>{t('modelSelector.topModels')}</span>
                      </div>
                      {featuredModels.map((model) => {
                        const isActive = model.id === currentModel;
                        return (
                          <div key={`featured-${model.id}`} style={{ position: 'relative' }}>
                            <button
                              className={clsx(
                                styles.modelDropdownItem,
                                { [styles.active]: isActive },
                                { [styles.locked]: model.isLocked }
                              )}
                              onClick={() => {
                                if (model.isLocked) { navigate('/pricing'); setIsMenuOpen(false); return; }
                                selectModel(model.provider, model.id);
                              }}
                              /* disabled handled via onClick + CSS */
                            >
                              <div className={clsx(styles.modelIconWrapper, { [styles.active]: isActive })}>
                                <model.providerIcon className={styles.modelDropdownIcon} />
                              </div>
                              <div className={styles.modelItemInfo}>
                                <span className={styles.modelItemLabel}>
                                  {model.name}
                                  <ModelStatusBadge modelId={model.id} modelType="video" context="selector" compact />
                                </span>
                                {model.infoChips.length > 0 && (
                                  <div className={styles.modelInfoRow}>
                                    {model.infoChips.map((chip, idx) => (
                                      <span key={idx} className={styles.modelInfoChip}>{chip.iconClass && <i className={chip.iconClass} />}{chip.text}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {isActive && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--selector-check-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', flexShrink: 0 }}><polyline points="20 6 9 17 4 12" /></svg>}
                            </button>
                            <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'auto' }}>
                              <AdminStatusButton modelId={model.id} modelType="video" modelDisplayName={model.name} isAdmin={isAdmin} compact />
                            </div>
                          </div>
                        );
                      })}
                      <div className={styles.modelSectionHeader}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                        <span>{t('modelSelector.allModels')}</span>
                      </div>
                    </>
                  )}
                  {sortedProviderGroups.map((group) => {
                    const config = VIDEO_MODELS_CONFIG[group.id];
                    const allModels = config?.models?.filter(m => m.available) || [];
                    const isActive = currentProviderGroupId === group.id || currentProvider === group.id;
                    const hasModels = allModels.length > 0;
                    // Provider is always clickable when it has any models — tier lock applies
                    // to individual models inside the drill-down, not the provider row.
                    const isClickable = hasModels;

                    return (
                      <button
                        key={group.id}
                        className={clsx(
                          styles.modelDropdownItem,
                          { [styles.active]: isActive },
                        )}
                        onClick={() => isClickable && handleProviderGroupClick(group)}
                        disabled={!isClickable}
                      >
                        <div className={clsx(styles.modelIconWrapper, { [styles.active]: isActive })}>
                          <group.icon className={styles.modelDropdownIcon} />
                        </div>
                        <div className={styles.modelItemInfo}>
                          <span className={styles.modelItemLabel}>{group.name}</span>
                        </div>
                        {hasModels && (
                          <span className={styles.modelMenuArrow}>›</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Second Level: Models in Provider Group */}
            {selectedProviderGroup && (
              <>
                <div className={styles.modelDropdownHeader}>
                  <button
                    className={styles.modelBackButton}
                    onClick={handleBackToProviders}
                    type="button"
                  >
                    ‹
                  </button>
                  <span className={styles.modelDropdownTitle}>
                    {PROVIDER_GROUPS.find(g => g.id === selectedProviderGroup)?.name}
                  </span>
                </div>
                <div className={styles.modelDropdownScroll}>
                  {selectedGroupModels
                    .sort((a, b) => {
                      if (a.isLocked === b.isLocked) return 0;
                      return a.isLocked ? 1 : -1;
                    })
                    .map((model) => {
                      const isActive = model.id === currentModel;

                      return (
                        <button
                          key={model.id}
                          className={clsx(
                            styles.modelDropdownItem,
                            { [styles.active]: isActive },
                            { [styles.locked]: model.isLocked }
                          )}
                          onClick={() => handleModelSelect(model.id, model.isLocked)}
                        >
                          <div className={styles.modelItemInfo}>
                            <span className={styles.modelItemLabel}>
                              {model.displayName}
                              {model.isLocked && (() => {
                                const minTier = getMinimumTier(model.id);
                                const badge = minTier ? TIER_BADGE_CONFIG[minTier] : null;
                                return badge ? (
                                  <span className={styles.modelTierBadge} style={{ color: badge.color, borderColor: badge.color }}>{badge.label}</span>
                                ) : null;
                              })()}
                              <ModelStatusBadge modelId={model.id} modelType="video" context="selector" compact />
                            </span>
                            {model.infoChips.length > 0 && (
                              <div className={styles.modelInfoRow}>
                                {model.infoChips.map((chip, idx) => (
                                  <span key={idx} className={styles.modelInfoChip}>{chip.iconClass && <i className={chip.iconClass} />}{chip.text}</span>
                                ))}
                              </div>
                            )}
                            {model.isLocked && (() => {
                              const minTier = getMinimumTier(model.id);
                              const tierName = minTier ? SUBSCRIPTION_TIERS[minTier]?.displayName || minTier : null;
                              return (
                                <span className={styles.modelLockedText}>
                                  {tierName ? t('modelSelector.availableWithTier', { tier: tierName }) : t('modelSelector.unavailableInSubscription')}
                                </span>
                              );
                            })()}
                          </div>
                          <AdminStatusButton
                            modelId={model.id}
                            modelType="video"
                            modelDisplayName={model.name}
                            isAdmin={isAdmin}
                            compact
                          />
                          {isActive && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--selector-check-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', flexShrink: 0 }}><polyline points="20 6 9 17 4 12" /></svg>}
                        </button>
                      );
                    })}
                </div>
              </>
            )}
            </>
          )}
          </div>

          {/* Desktop: Sub-flyout for hovered provider's models (not in edit mode) */}
          {!editMode && isDesktop && hoveredProvider && (() => {
            const group = PROVIDER_GROUPS.find(g => g.id === hoveredProvider);
            if (!group) return null;
            const config = VIDEO_MODELS_CONFIG[group.id];
            const allModels = config?.models?.filter(m => m.available) || [];
            if (allModels.length === 0) return null;
            const models = allModels.map(m => ({
              ...m,
              isLocked: !isModelAvailable(m.id) || isModelUnderMaintenance(m.id, 'video'),
              displayName: getModelDisplayName(m.id, group.id),
              infoChips: getModelInfo(m.id, group.id)
            }));
            return (
              <div
                className={styles.subFlyout}
                style={{ top: subFlyoutPos.top, left: subFlyoutPos.left, transform: 'translateY(-100%)', maxHeight: `${subFlyoutPos.top - 16}px` }}
                onMouseEnter={handleSubFlyoutEnter}
                onMouseLeave={handleSubFlyoutLeave}
              >
                <div className={styles.subFlyoutScroll}>
                  {models
                    .sort((a, b) => a.isLocked === b.isLocked ? 0 : a.isLocked ? 1 : -1)
                    .map((model) => {
                      const isActive = model.id === currentModel;
                      return (
                        <div key={model.id} style={{ position: 'relative' }}>
                          <button
                            className={clsx(
                              styles.subFlyoutItem,
                              { [styles.subFlyoutItemActive]: isActive },
                              { [styles.subFlyoutItemLocked]: model.isLocked }
                            )}
                            onClick={() => {
                              if (model.isLocked) { navigate('/pricing'); setIsMenuOpen(false); return; }
                              selectModel(hoveredProvider!, model.id);
                            }}
                            /* disabled handled via onClick + CSS */
                          >
                            <div className={styles.subFlyoutItemInfo}>
                              <span className={styles.subFlyoutItemName}>
                                {model.displayName}
                                {model.isLocked && (() => {
                                  const minTier = getMinimumTier(model.id);
                                  const badge = minTier ? TIER_BADGE_CONFIG[minTier] : null;
                                  return badge ? (
                                    <span className={styles.modelTierBadge} style={{ color: badge.color, borderColor: badge.color }}>{badge.label}</span>
                                  ) : null;
                                })()}
                                <ModelStatusBadge modelId={model.id} modelType="video" context="selector" compact />
                              </span>
                              {model.infoChips.length > 0 && (
                                <div className={styles.subFlyoutChips}>
                                  {model.infoChips.map((chip, idx) => (
                                    <span key={idx} className={styles.subFlyoutChip}>{chip.iconClass && <i className={chip.iconClass} />}{chip.text}</span>
                                  ))}
                                </div>
                              )}
                              {model.isLocked && (() => {
                                const minTier = getMinimumTier(model.id);
                                const tierName = minTier ? SUBSCRIPTION_TIERS[minTier]?.displayName || minTier : null;
                                return (
                                  <span className={styles.subFlyoutLockedText}>
                                    {tierName ? t('modelSelector.availableWithTier', { tier: tierName }) : t('modelSelector.unavailable')}
                                  </span>
                                );
                              })()}
                            </div>
                            {isActive && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--selector-check-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', flexShrink: 0 }}><polyline points="20 6 9 17 4 12" /></svg>}
                          </button>
                          <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'auto' }}>
                            <AdminStatusButton modelId={model.id} modelType="video" modelDisplayName={model.name} isAdmin={isAdmin} compact />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })()}
        </>,
        document.body
      )}
    </>
  );
};

export default VideoModelSelectorChip;
